import type { CustomerOverview } from '@vy/api-definition';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import React from 'react';
import { isEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import { saveAs } from 'file-saver';
import { vyDate } from '@vy/utils';

import { useNonPagedCustomersOverviewsFromParams } from '../../../../hooks/requests/use-customers-overviews/use-non-paged-customers-overviews-from-params';
import { runMutation } from '../../../../utils/vy-mutation';
import { generateCSV } from '../customers-page-utils/customers-page-generate-csv';

type UseCustomersActionsProps = {
  selectedCustomers: CustomerOverview[];
  apiRef?: React.MutableRefObject<GridApiPro>;
};

export const useCustomersActions = ({ selectedCustomers, apiRef }: UseCustomersActionsProps) => {
  const allCustomersMutation = useNonPagedCustomersOverviewsFromParams();

  const handleExportSelectedCustomers = async () => {
    const shouldExportAll = isEmptyArray(selectedCustomers);
    const customersToExport = await getCustomersToExport(shouldExportAll);

    let visibleFields: string[] | undefined;
    if (isNotNullOrUndefined(apiRef?.current)) {
      visibleFields = apiRef.current.getVisibleColumns().map((column) => column.field);
    }

    const csvContent = generateCSV(customersToExport ?? [], visibleFields);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

    const fileName = vyDate().format('MMMM, DD, YYYY');
    saveAs(blob, `${fileName}.csv`);
  };

  const getCustomersToExport = async (exportAll: boolean) => {
    if (!exportAll) {
      return selectedCustomers;
    }

    const allCustomersResponse = await runMutation(allCustomersMutation, undefined, {
      pending: 'Downloading all customers...',
      success: 'All customers downloaded',
      error: 'Failed to download all customers',
    });

    return allCustomersResponse?.items ?? [];
  };

  return {
    handleExportSelectedCustomers,
  };
};
